import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import logo from '../../assets/pictures/Logo-bw.png';

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
        maxWidth: '450px',
    },
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    ...theme.applyStyles('dark', {
        boxShadow:
            'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
}));

const VerificationContainer = styled(Stack)(({ theme }) => ({
    height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
    minHeight: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        zIndex: -1,
        inset: 0,
        backgroundImage:
            'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
        backgroundRepeat: 'no-repeat',
        ...theme.applyStyles('dark', {
            backgroundImage:
                'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
        }),
    },
}));

const CodeInput = styled('input')(({ theme, error }) => ({
    width: '60px',
    height: '60px',
    fontSize: '24px',
    fontWeight: 'bold',
    textAlign: 'center',
    border: `2px solid ${error ? '#d32f2f' : '#e0e0e0'}`,
    borderRadius: '12px',
    outline: 'none',
    backgroundColor: '#fff',
    transition: 'all 0.2s ease-in-out',
    '&:focus': {
        borderColor: error ? '#d32f2f' : '#4a148c',
        boxShadow: `0 0 0 2px ${error ? 'rgba(211, 47, 47, 0.2)' : 'rgba(74, 20, 140, 0.2)'}`,
    },
    '&:hover': {
        borderColor: error ? '#d32f2f' : '#bdbdbd',
    },
    [theme.breakpoints.down('sm')]: {
        width: '50px',
        height: '50px',
        fontSize: '20px',
    },
}));

const CodeContainer = styled(Box)({
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    marginBottom: '24px',
});

export default function ResetPasswordVerificationCode() {
    const [code, setCode] = useState(['', '', '', '', '']);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    
    const inputRefs = useRef([]);
    const navigate = useNavigate();
    const { email } = useParams(); // Asumiendo que el email viene como parámetro

    useEffect(() => {
        // Focus en el primer input al cargar el componente
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const handleInputChange = (index, value) => {
        // Solo permitir números
        if (!/^\d*$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        setError(false);
        setErrorMessage('');

        // Auto-focus al siguiente input si se ingresó un número
        if (value && index < 4) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, event) => {
        // Manejar backspace para ir al input anterior
        if (event.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        
        // Manejar flechas para navegación
        if (event.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        
        if (event.key === 'ArrowRight' && index < 4) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const pastedText = event.clipboardData.getData('text');
        const numbers = pastedText.replace(/\D/g, '').slice(0, 5);
        
        if (numbers.length > 0) {
            const newCode = [...code];
            for (let i = 0; i < numbers.length && i < 5; i++) {
                newCode[i] = numbers[i];
            }
            setCode(newCode);
            
            // Focus en el último input completado o el siguiente vacío
            const nextIndex = Math.min(numbers.length, 4);
            inputRefs.current[nextIndex]?.focus();
        }
    };

    const isCodeComplete = () => {
        return code.every(digit => digit !== '');
    };

    const handleVerifyCode = async () => {
        if (!isCodeComplete()) {
            setError(true);
            setErrorMessage('Por favor, ingrese el código completo de 5 dígitos.');
            return;
        }

        setLoading(true);
        setError(false);
        setErrorMessage('');

        const verificationCode = code.join('');
        const verificationCodeToken = localStorage.getItem('verificationCodeToken');
        if (!verificationCodeToken) {
            setError(true);
            setErrorMessage('Token de verificación no encontrado.');
            return;
        }

        try {
            const response = await fetch('https://dprincess-training.servehttp.com/verify-reset-password-code', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${verificationCodeToken}`
                },
                body: JSON.stringify({ 
                    code: verificationCode 
                }),
            });

            const result = await response.json();

            if (response.ok) {
                setSuccessMessage('¡Código verificado correctamente! Redirigiendo para cambiar su contraseña...');
                setShowSuccess(true);

                localStorage.setItem('resetPasswordToken', result.token);
                
                // Redirigir después de 3 segundos
                setTimeout(() => {
                    navigate(`/reset-password/change-password?token=${result.token}`);
                }, 3000);
            } else {
                setError(true);
                setErrorMessage(result.message || result.error || 'Código de verificación incorrecto. Por favor, inténtelo de nuevo.');
                // Limpiar el código en caso de error
                setCode(['', '', '', '', '']);
                inputRefs.current[0]?.focus();
            }
        } catch (error) {
            console.error('Error de red:', error);
            setError(true);
            setErrorMessage('Error al conectar con el servidor. Por favor, inténtelo de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        try {
            setLoading(true);
            const response = await fetch('https://dprincess-training.servehttp.com/resend-reset-code', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    email: email || localStorage.getItem('resetEmail')
                }),
            });

            if (response.ok) {
                setSuccessMessage('Código reenviado exitosamente. Revise su correo electrónico.');
                setShowSuccess(true);
                setCode(['', '', '', '', '']);
                inputRefs.current[0]?.focus();
            }
        } catch (error) {
            console.error('Error al reenviar código:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <CssBaseline enableColorScheme />
            <VerificationContainer direction="column" justifyContent="space-between">
                <Card variant="outlined">
                    <CardMedia
                        component='img'
                        image={logo}
                        alt='Logo'
                        sx={{ width: 100, height: 85, objectFit: 'contain', margin: 'auto' }}
                    />
                    <Typography
                        component="h1"
                        variant="h4"
                        sx={{ 
                            width: '100%', 
                            fontSize: 'clamp(1.5rem, 8vw, 1.8rem)', 
                            textAlign: 'center', 
                            marginBottom: 2, 
                            color: '#4a148c', 
                            fontWeight: 'bold' 
                        }}
                    >
                        Verificación de Código
                    </Typography>
                    
                    <Typography
                        variant="body1"
                        sx={{ 
                            textAlign: 'center', 
                            marginBottom: 3, 
                            color: '#666',
                            fontSize: '14px'
                        }}
                    >
                        Ingrese el código de 5 dígitos enviado a su correo electrónico
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <FormControl>
                            <FormLabel sx={{ textAlign: 'center', marginBottom: 2, fontWeight: 'bold' }}>
                                Código de Verificación
                            </FormLabel>
                            <CodeContainer>
                                {code.map((digit, index) => (
                                    <CodeInput
                                        key={index}
                                        ref={el => inputRefs.current[index] = el}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength="1"
                                        value={digit}
                                        error={error}
                                        onChange={(e) => handleInputChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        onPaste={handlePaste}
                                        disabled={loading}
                                    />
                                ))}
                            </CodeContainer>
                        </FormControl>

                        {error && (
                            <Typography 
                                variant="body2" 
                                sx={{ color: '#d32f2f', textAlign: 'center', fontSize: '14px' }}
                            >
                                {errorMessage}
                            </Typography>
                        )}

                        <Button
                            type="button"
                            variant="contained"
                            onClick={handleVerifyCode}
                            disabled={loading || !isCodeComplete()}
                            sx={{ 
                                backgroundColor: '#4a148c',
                                color: '#fff',
                                '&:hover': {
                                    backgroundColor: '#6a1b9a',
                                },
                                '&:disabled': {
                                    backgroundColor: '#e0e0e0',
                                    color: '#9e9e9e',
                                },
                                padding: '12px',
                                fontWeight: 'bold',
                                position: 'relative'
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} sx={{ color: '#fff' }} />
                            ) : (
                                'Verificar Código'
                            )}
                        </Button>

                        <Typography 
                            variant="body2" 
                            sx={{ textAlign: 'center', marginTop: 2 }}
                        >
                            ¿No recibió el código?{' '}
                            <Button
                                variant="text"
                                onClick={handleResendCode}
                                disabled={loading}
                                sx={{ 
                                    color: '#4a148c', 
                                    textDecoration: 'none',
                                    padding: 0,
                                    minWidth: 'auto',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline',
                                    }
                                }}
                            >
                                Reenviar código
                            </Button>
                        </Typography>
                    </Box>
                </Card>

                <Snackbar
                    open={showSuccess}
                    autoHideDuration={3000}
                    onClose={() => setShowSuccess(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={() => setShowSuccess(false)} 
                        severity="success" 
                        sx={{ width: '100%' }}
                    >
                        {successMessage}
                    </Alert>
                </Snackbar>
            </VerificationContainer>
        </div>
    );
}
